import React from 'react';
import { Alert, Box, Container, LinearProgress, Paper, Typography } from '@mui/material';
import { Gavel as GavelIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

import { useWitnessCompanyRegistrationForm } from '../../hooks/witness-company/use-witness-company-registration-form';
import { RepresentativesSection } from '../representatives-section';

import { WitnessInfoSection } from './witness-info-section';

import type { WitnessCompanyRegistrationFormProps } from '../../types/company.types';

import { FixedFooter } from '@/components/footer/fixed-footer';

export const WitnessCompanyRegistrationForm: React.FC<WitnessCompanyRegistrationFormProps> = ({
  onSubmit,
  onCancel,
}) => {
  const {
    formData,
    validationState,
    updateField,
    addRepresentative,
    updateRepresentative,
    removeRepresentative,
    getFieldError,
    isFieldValid,
    getSalesPersonOptions,
    canAddRepresentative,
    isSubmitDisabled,
    handleSubmit,
    departments,
    departmentsLoading,
    staffsLoading,
    handleDepartmentChange,
  } = useWitnessCompanyRegistrationForm(onSubmit);

  const handleFormSubmit = async () => {
    await handleSubmit();
  };

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: '#002A76', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GavelIcon sx={{ fontSize: 40, mr: 2 }} />
          <Box>
            <Typography
              variant='h3'
              fontWeight='bold'
              sx={{
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              }}
            >
              立会会社登録フォーム
            </Typography>
            <Typography
              variant='h6'
              sx={{ opacity: 0.9, mt: 1, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
            >
              立会会社情報を入力してください
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Loading Indicator */}
      {validationState.isSubmitting && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* General Error Messages */}
      {validationState.errors.some((error) => error.field === 'general') && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {validationState.errors.find((error) => error.field === 'general')?.message}
        </Alert>
      )}

      <Box
        component='form'
        onSubmit={(event) => {
          event.preventDefault();
          void handleFormSubmit();
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {/* Witness Company Information Section */}
          <WitnessInfoSection
            formData={formData}
            onUpdateField={updateField}
            getFieldError={getFieldError}
            isFieldValid={isFieldValid}
          />

          {/* Representatives Section */}
          <RepresentativesSection
            representatives={formData.representatives}
            onAddRepresentative={addRepresentative}
            onRemoveRepresentative={removeRepresentative}
            onUpdateRepresentative={updateRepresentative}
            getSalesPersonOptions={getSalesPersonOptions}
            getFieldError={getFieldError}
            isFieldValid={isFieldValid}
            canAddRepresentative={canAddRepresentative}
            departments={departments}
            departmentsLoading={departmentsLoading}
            staffsLoading={staffsLoading}
            onDepartmentChange={handleDepartmentChange}
          />

          {/* Add bottom padding to prevent content from being hidden behind fixed footer */}
          <Box sx={{ height: '100px' }} />
          {/* Fixed Action Footer */}
          <FixedFooter
            buttons={[
              {
                label: 'キャンセル',
                onClick: onCancel,
                variant: 'outlined',
                startIcon: <CancelIcon />,
                disabled: validationState.isSubmitting,
                sx: {
                  marginLeft: 'auto',
                },
              },
              {
                label: validationState.isSubmitting ? '登録中...' : '入力内容を決定',
                onClick: handleFormSubmit,
                variant: 'contained',
                startIcon: <SaveIcon />,
                disabled: isSubmitDisabled,
                loading: validationState.isSubmitting,
              },
            ]}
            isSubmitting={validationState.isSubmitting}
            hasNavigationPanel={false}
          />
        </Box>
      </Box>
    </Container>
  );
};
